(() => {
  "use strict";

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const REQUIRED_FIELDS = ["contactName", "phone", "email", "message"];
  const RATE_LIMIT_MESSAGE =
    "Has realizado demasiados intentos. Espera un momento y vuelve a intentarlo.";
  const TURNSTILE_MESSAGE =
    "No pudimos validar que eres una persona. Inténtalo de nuevo.";
  const TURNSTILE_UNAVAILABLE_MESSAGE =
    "No pudimos validar tu solicitud en este momento. Espera un momento y vuelve a intentarlo.";
  const SUCCESS_MESSAGE =
    "Gracias. Recibimos tu mensaje y te contactaremos pronto.";

  const FIELD_LABELS = {
    contactName: "Nombre de contacto",
    phone: "WhatsApp/celular",
    email: "Email",
    message: "Mensaje",
  };

  const trimValue = (value) => String(value || "").trim();

  const getField = (form, name) =>
    form.querySelector(`[data-field="${name}"]`);

  const getFieldWrap = (field) =>
    field?.closest(".peguu-contact__field");

  const clearFieldErrors = (form) => {
    form.querySelectorAll(".peguu-contact__field.is-invalid").forEach((wrap) => {
      wrap.classList.remove("is-invalid");
    });

    form.querySelectorAll("[data-field][aria-invalid]").forEach((field) => {
      field.removeAttribute("aria-invalid");
    });
  };

  const markInvalid = (field) => {
    if (!field) {
      return;
    }

    field.setAttribute("aria-invalid", "true");
    getFieldWrap(field)?.classList.add("is-invalid");
  };

  const showStatus = (status, message, type) => {
    if (!status) {
      return;
    }

    status.hidden = !message;
    status.textContent = message || "";
    status.classList.toggle("is-error", type === "error");
    status.classList.toggle("is-success", type === "success");
  };

  const setSubmitting = (button, isSubmitting) => {
    if (!button) {
      return;
    }

    button.disabled = isSubmitting;
    button.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    button.classList.toggle("is-loading", isSubmitting);
  };

  const collectPayload = (form) => {
    const values = {
      contactName: trimValue(getField(form, "contactName")?.value),
      phone: trimValue(getField(form, "phone")?.value),
      company: trimValue(getField(form, "company")?.value),
      email: trimValue(getField(form, "email")?.value),
      message: trimValue(getField(form, "message")?.value),
    };

    const invalidFields = [];

    REQUIRED_FIELDS.forEach((name) => {
      if (!values[name]) {
        invalidFields.push(name);
      }
    });

    if (values.email && !EMAIL_PATTERN.test(values.email)) {
      invalidFields.push("email");
    }

    if (invalidFields.length) {
      const uniqueFields = [...new Set(invalidFields)];
      const error = new Error("Completa los campos obligatorios.");

      error.invalidFields = uniqueFields;
      throw error;
    }

    const payload = {
      contactName: values.contactName,
      phone: values.phone,
      email: values.email,
      message: values.message,
    };

    if (values.company) {
      payload.company = values.company;
    }

    return payload;
  };

  const parseApiResponse = async (response) => {
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    return payload;
  };

  const readApiMessage = (payload) => {
    const candidates = [
      payload?.message,
      payload?.error?.message,
      payload?.error,
    ];

    return candidates.find(
      (value) => typeof value === "string" && value.trim()
    );
  };

  const readApiErrorCode = (payload) => {
    const candidates = [
      payload?.code,
      payload?.error?.code,
      payload?.error,
    ];

    return candidates.find(
      (value) => typeof value === "string" && value.trim()
    );
  };

  const getApiErrorMessage = (response, payload) => {
    const errorCode = readApiErrorCode(payload);

    if (response.status === 429) {
      return RATE_LIMIT_MESSAGE;
    }

    if (
      response.status === 403 &&
      ["TURNSTILE_TOKEN_REQUIRED", "TURNSTILE_FAILED"].includes(errorCode)
    ) {
      return TURNSTILE_MESSAGE;
    }

    if (response.status === 503) {
      return TURNSTILE_UNAVAILABLE_MESSAGE;
    }

    const apiMessage = readApiMessage(payload);

    if (apiMessage) {
      return apiMessage.trim();
    }

    if (response.status === 400) {
      return "Revisa los datos del formulario e inténtalo de nuevo.";
    }

    return "No fue posible enviar tu mensaje. Inténtalo de nuevo.";
  };

  const submitInquiry = async (form, payload) => {
    const section = form.closest("[data-peguu-contact]");
    const apiUrl = window.PeguuApi.resolve(
      section?.dataset.apiUrl || window.PeguuApi.paths.corporateInquiries
    );

    if (!apiUrl) {
      throw new Error("No está configurada la API de contacto.");
    }

    const challenge = await window.PeguuTurnstile.getToken();

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...payload,
          turnstileToken: challenge.token,
        }),
      });

      const payloadResponse = await parseApiResponse(response);

      if (!response.ok || payloadResponse?.success === false) {
        const error = new Error(getApiErrorMessage(response, payloadResponse));
        error.status = response.status;
        error.code = readApiErrorCode(payloadResponse);
        throw error;
      }

      return payloadResponse;
    } finally {
      challenge.reset();
    }
  };

  const bindForm = (form) => {
    if (form.dataset.corporateInquiryBound === "true") {
      return;
    }

    form.dataset.corporateInquiryBound = "true";

    const status = form.querySelector("[data-contact-status]");
    const submitButton = form.querySelector("[data-contact-submit]");

    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      if (submitButton?.disabled) {
        return;
      }

      clearFieldErrors(form);
      showStatus(status, "", "");

      try {
        const payload = collectPayload(form);

        setSubmitting(submitButton, true);
        await submitInquiry(form, payload);

        form.reset();
        showStatus(status, SUCCESS_MESSAGE, "success");
      } catch (error) {
        const invalidFields = error?.invalidFields || [];

        invalidFields.forEach((name) => {
          markInvalid(getField(form, name));
        });

        if (invalidFields.length) {
          window.PeguuValidationAlert?.open({
            title: "Faltan datos para continuar",
            message: "Para continuar, completa los campos de:",
            fields: invalidFields.map((name) => FIELD_LABELS[name] || name),
          });
          showStatus(status, "", "");
        } else {
          showStatus(
            status,
            error?.message || "No fue posible enviar tu mensaje. Inténtalo de nuevo.",
            "error"
          );
        }
      } finally {
        setSubmitting(submitButton, false);
      }
    });
  };

  const init = () => {
    document
      .querySelectorAll("[data-corporate-inquiry-form]")
      .forEach(bindForm);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
